import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ExceptionCard } from "@/components/ExceptionCard";

const EASE = [0.16, 1, 0.3, 1];

const lines = [
  { text: "Shipping exceptions,", mint: false },
  { text: "solved.", mint: true },
  { text: "Not just spotted.", mint: false },
];

const wrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const line = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: EASE } },
};

export const Hero = ({ onCta }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 22);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 18);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] items-center overflow-hidden pt-16"
    >
      <div className="diag-lines absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full bg-mint/10 blur-[140px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-10 px-4 pb-24 pt-10 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-6 lg:px-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.2 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.24em] text-mint"
            data-testid="hero-eyebrow"
          >
            Pre-launch · For Shopify brands
          </motion.p>

          <motion.h1
            variants={wrap}
            initial="hidden"
            animate="show"
            data-testid="hero-headline"
            className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          >
            {lines.map((l) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  variants={line}
                  className={`block ${l.mint ? "text-mint" : ""}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
            data-testid="hero-subheadline"
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            Truvon detects delays, tells your customers before they ask, files
            the carrier claim, and prevents WISMO tickets — automatically.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.25 }}
            className="mt-10"
          >
            <div className="relative inline-block">
              <div
                className="cta-glow absolute -inset-3 rounded-full bg-mint/30 blur-2xl"
                aria-hidden="true"
              />
              <button
                onClick={onCta}
                data-testid="hero-cta-button"
                className="group relative flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 font-display text-base font-bold text-ink transition-colors duration-200 hover:bg-mint-hover"
              >
                Get early access
                <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, ease: EASE, delay: 1.1 }}
          className="relative mx-auto w-full max-w-sm lg:max-w-md"
          style={{ x: sx, y: sy }}
        >
          <div
            className="absolute inset-0 scale-90 rounded-full bg-mint/15 blur-[90px]"
            aria-hidden="true"
          />
          <div className="drift relative">
            <ExceptionCard />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
